import React, { useState, useEffect } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, IconButton, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tabs, Tab, CircularProgress, Divider } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, PlayCircleOutline, Visibility, Close, PlayArrow, Stop, QuestionAnswer, Group } from '@mui/icons-material';
import Loader from '../Loader';
import CustomTextField from '../CustomTextField';
import CustomDropDown from '../CustomDropDown';
import StatsBarCard from '../StatsBar/StatsBarCard';
import AddCourseDialog from './AddCourseDialog';
import CourseQuestionsDialog from './CourseQuestionsDialog';
import AssessmentConfiguration from '../Training/Batch/AssessmentConfiguration';
import HttpService from '../../backend/requestApis';
import { courses, coursesStatus, getWORKerQuizAttemptsTiles, getWORKerQuizAttemptsTable } from '../../backend/backendApis';
import { headingBlue, lightGrey, maroon, mediumGrey, pureWhite, red } from '../../assets/colors';
import { tableOffsets, WORKerTypesList } from '../../utils/constants';
import { tableHeaderCellStyle, buttonStyle } from '../../utils/commonCSS';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../CustomComponents/CustomComponentsSlice';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`courses-tabpanel-${index}`}
      aria-labelledby={`courses-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Courses = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);

  // Existing Courses Tab States
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(10);
  const [filterBy, setFilterBy] = useState('');
  const [data, setData] = useState({ data: [], total_count: 0, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Results Tab States
  const [selectedCourseName, setSelectedCourseName] = useState('All Courses & Quizzes');
  const [selectedCourseId, setSelectedCourseId] = useState('all');
  const [allCoursesForDropdown, setAllCoursesForDropdown] = useState([]);
  
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsOffset, setResultsOffset] = useState(10);
  const [resultsData, setResultsData] = useState({ data: [], total_count: 0, total_pages: 1 });
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [resultsFilterBy, setResultsFilterBy] = useState('');

  const [tilesData, setTilesData] = useState({
    totalAttempts: 0,
    inProgressAttempts: 0,
    passedAttempts: 0,
    failedAttempts: 0
  });
  const [isTilesLoading, setIsTilesLoading] = useState(false);

  // Dialog triggers
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [isQuestionsOpen, setIsQuestionsOpen] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch Courses
  const fetchCoursesList = async () => {
    try {
      setIsLoading(true);
      const options = {
        url: courses,
        method: 'GET',
        params: {
          page: page,
          offset: offset,
          search: filterBy || undefined
        },
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };

      const resp = await HttpService(options);
      if (resp?.data?.status === 1 || resp?.data?.data) {
        setData(resp.data.data);
      }
    } catch (err) {
      console.error('Error fetching courses list: ', err);
      if (err?.response?.status === 403 || err?.status === 403) {
        localStorage.clear();
        window.location.href = '/Login';
      } else {
        dispatch(showSnackBar({ message: 'Error fetching courses. Please try again.', severity: 'error' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCoursesList();
  }, [page, offset, refreshTrigger]);

  // Debounced search trigger for Courses List
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPage(1);
      fetchCoursesList();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [filterBy]);

  // Fetch Courses list for Results dropdown
  const fetchAllCoursesForDropdown = async () => {
    try {
      const options = {
        url: courses,
        method: 'GET',
        params: {
          page: 1,
          offset: 1000
        },
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };
      const resp = await HttpService(options);
      if (resp?.data?.status === 1 || resp?.data?.data) {
        const quizzes = (resp.data.data.data || []).filter(c => c.isQuiz);
        setAllCoursesForDropdown(quizzes);
      }
    } catch (err) {
      console.error('Error fetching all courses for dropdown: ', err);
    }
  };

  useEffect(() => {
    fetchAllCoursesForDropdown();
  }, []);

  // Fetch tiles and table data for Results Tab
  const fetchTilesData = async () => {
    try {
      setIsTilesLoading(true);
      const options = {
        url: getWORKerQuizAttemptsTiles,
        method: 'GET',
        params: {
          courseId: selectedCourseId !== 'all' ? selectedCourseId : undefined
        },
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };
      const resp = await HttpService(options);
      if (resp?.data?.status === 1 || resp?.data?.data) {
        setTilesData(resp.data.data);
      }
    } catch (err) {
      console.error('Error fetching tiles data: ', err);
    } finally {
      setIsTilesLoading(false);
    }
  };

  const fetchTableData = async () => {
    try {
      setIsResultsLoading(true);
      const options = {
        url: getWORKerQuizAttemptsTable,
        method: 'GET',
        params: {
          courseId: selectedCourseId !== 'all' ? selectedCourseId : undefined,
          page: resultsPage,
          limit: resultsOffset,
          search: resultsFilterBy || undefined
        },
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
        }
      };
      const resp = await HttpService(options);
      if (resp?.data?.status === 1 || resp?.data?.data) {
        setResultsData(resp.data.data);
      }
    } catch (err) {
      console.error('Error fetching table data: ', err);
      dispatch(showSnackBar({ message: 'Error fetching quiz attempts list.', severity: 'error' }));
    } finally {
      setIsResultsLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1) {
      fetchTilesData();
    }
  }, [tabValue, selectedCourseId]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchTableData();
    }
  }, [tabValue, selectedCourseId, resultsPage, resultsOffset]);

  // Debounced search trigger for Results List
  useEffect(() => {
    if (tabValue === 1) {
      const delayDebounce = setTimeout(() => {
        setResultsPage(1);
        fetchTableData();
      }, 500);
      return () => clearTimeout(delayDebounce);
    }
  }, [resultsFilterBy]);

  const updatePage = (newPage) => {
    setPage(newPage);
  };

  const updateOffset = (newOffset) => {
    setPage(1);
    setOffset(newOffset);
  };

  const updateResultsPage = (newPage) => {
    setResultsPage(newPage);
  };

  const updateResultsOffset = (newOffset) => {
    setResultsPage(1);
    setResultsOffset(newOffset);
  };

  const getApplicableForLabel = (row) => {
    const applicableFor = row.applicableFor;
    if (!applicableFor || applicableFor.length === 0) return 'All';
    return (Array.isArray(applicableFor) ? applicableFor : [applicableFor])
      .map(val => {
        if (val === 'public') return 'Public';
        const match = WORKerTypesList.find(t => t.value === val);
        return match ? match.label : val;
      })
      .join(', ');
  };

  const handleUpdateStatus = (courseId, newStatus) => {
    setIsLoading(true);
    const options = {
      url: coursesStatus,
      method: 'POST',
      payload: {
        courseId,
        status: newStatus
      },
      headers: {
        headers: {
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
        }
      }
    };
    HttpService(options)
      .then(() => {
        setIsLoading(false);
        dispatch(showSnackBar({ message: `Successfully updated course status to ${newStatus}.`, severity: 'success' }));
        setRefreshTrigger(prev => prev + 1);
      })
      .catch((err) => {
        setIsLoading(false);
        console.error('Error updating course status: ', err);
        dispatch(showSnackBar({ message: err?.response?.data?.message || 'Error updating course status. Please try again.', severity: 'error' }));
      });
  };

  const paperStyle = {
    border: '1px solid #D9D9D9',
    padding: '0.2rem 0.8rem 0.2rem 0.8rem',
    borderRadius: '8px',
    backgroundColor: pureWhite
  };

  const headerBoxStyle = {
    display: 'flex',
    padding: '0.2rem 1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem'
  };

  const tableHeaderStyle = {
    ...tableHeaderCellStyle,
    backgroundColor: lightGrey,
    fontWeight: 'bold',
    color: headingBlue,
    borderBottom: `2px solid ${mediumGrey}`
  };

  return (
    <>
      {/* Combined Tabs & Action Bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, backgroundColor: pureWhite }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          TabIndicatorProps={{ style: { backgroundColor: headingBlue, height: '3px', borderRadius: '3px 3px 0 0' } }}
          sx={{ 
            minHeight: '2.8rem',
            '& .MuiTab-root': { 
              fontSize: '0.95rem', 
              textTransform: 'none', 
              fontWeight: 600, 
              color: '#888', 
              padding: '0.4rem 1.5rem',
              minHeight: '2.8rem',
              transition: '0.3s ease',
            },
            '& .MuiTab-root:hover': {
              color: headingBlue,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
            '& .Mui-selected': { color: `${headingBlue} !important` }
          }}
        >
          <Tab label="Courses & Quizzes" />
          <Tab label="Results" />
        </Tabs>
        <Box style={{ display: 'flex', gap: '0.5rem' }}>
          {localStorage.getItem('designation') === 'admin' && tabValue === 0 && (
            <Button 
              variant="outlined" 
              style={{ backgroundColor: headingBlue, color: pureWhite, textTransform: 'capitalize', fontFamily: 'Helvetica', fontWeight: '500', fontSize: '0.9rem', padding: '0.4rem 1.5rem', borderRadius: '4px' }}
              onClick={() => setIsAddOpen(true)}
            >
              Add New Course/ Quiz
            </Button>
          )}
        </Box>
      </Box>

      {/* Dropdown Filter on Top of Tiles */}
      {tabValue === 1 && (
        <>
          <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.4rem 2.5rem', backgroundColor: pureWhite }}>
            <CustomDropDown
              placeHolder="Select Course/Quiz"
              list={[
                { name: 'All Courses & Quizzes', value: 'all' },
                ...allCoursesForDropdown.map(c => ({ name: c.courseName, value: c._id }))
              ]}
              value={selectedCourseName}
              setFunc={(val) => {
                setSelectedCourseName(val.name);
                setSelectedCourseId(val.value);
                setResultsPage(1);
              }}
              isValueRequired={true}
              width="15rem"
            />
          </Box>
          <Divider sx={{ borderColor: '#E0E0E0' }} />
        </>
      )}

      {/* Main Content Area */}
      <Box style={{ overflow: 'auto', padding: "0.3rem 1.5rem 0rem 1.5rem" }}>
        {/* Tab 1: Courses & Quizzes list */}
        <TabPanel value={tabValue} index={0}>
            {/* Header filter options */}
            <Box style={headerBoxStyle}>
              <Typography variant="h6" style={{ fontWeight: 600, color: headingBlue }}>
                Courses & Quizzes List
              </Typography>
              <Box style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <CustomTextField
                  placeHolder="Enter Text to Filter"
                  value={filterBy}
                  setFunc={setFilterBy}
                />
              </Box>
            </Box>

            <Box style={{ display: 'flex', justifyContent: 'center', overflow: 'auto', position: 'relative' }}>
              {isLoading && <Loader />}
              <TableContainer
                component={Paper}
                sx={{
                  width: '98%',
                  height: '60vh',
                  border: '1px solid #D9D9D9',
                  marginTop: '0.2rem',
                  borderRadius: '6px',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none'
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={tableHeaderStyle}>Unique ID</TableCell>
                      <TableCell style={tableHeaderStyle}>Course Name</TableCell>
                      <TableCell style={tableHeaderStyle}>Description</TableCell>
                      <TableCell style={tableHeaderStyle}>Applicable For</TableCell>
                      <TableCell style={tableHeaderStyle}># Videos</TableCell>
                      <TableCell style={tableHeaderStyle}>Is Quiz?</TableCell>
                      <TableCell style={tableHeaderStyle}>Attempts Allowed</TableCell>
                      <TableCell style={tableHeaderStyle}>Status</TableCell>
                      <TableCell style={tableHeaderStyle}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" style={{ padding: '2rem', color: '#888' }}>
                          No courses found. Click "Add New Course/ Quiz" to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.data?.map((row, idx) => (
                        <TableRow key={row._id || idx} hover>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem', color: headingBlue, fontWeight: 600 }}>
                            {row.uniqueId || '-'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem', fontWeight: 500 }}>
                            {row.courseName || '-'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.85rem', maxWidth: '15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description}>
                            {row.description || '-'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.85rem' }}>
                            {getApplicableForLabel(row)}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                            {row.videos?.length || 0}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem', color: row.isQuiz ? headingBlue : '#888' }}>
                            {row.isQuiz ? 'Yes' : 'No'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                            {row.isQuiz ? row.attemptsAllowed : '-'}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                            {(() => {
                              const status = row.status || 'scheduled';
                              let color = '#777';
                              let text = 'Scheduled';
                              if (status === 'active') {
                                color = '#2e7d32'; // green
                                text = 'Active';
                              } else if (status === 'closed') {
                                color = '#c62828'; // red
                                text = 'Closed';
                              } else {
                                color = '#1565c0'; // blue
                              }
                              return (
                                <Box sx={{
                                  display: 'inline-block',
                                  border: `1px solid ${color}`,
                                  color: color,
                                  borderRadius: '4px',
                                  px: '0.4rem',
                                  py: '0.1rem',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  backgroundColor: `${color}0b`
                                }}>
                                  {text}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center" style={{ padding: '0.2rem 0.5rem' }}>
                            <Box style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  style={{ color: headingBlue }}
                                  onClick={() => {
                                    setSelectedCourse(row);
                                    setIsDetailsOpen(true);
                                    setActiveVideoUrl('');
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {row.isQuiz && (
                                <Tooltip title="Manage Questions">
                                  <IconButton
                                    size="small"
                                    style={{ color: headingBlue }}
                                    onClick={() => {
                                      setSelectedCourse(row);
                                      setIsQuestionsOpen(true);
                                    }}
                                  >
                                    <QuestionAnswer fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {(() => {
                                const status = row.status || 'scheduled';
                                if (status === 'scheduled') {
                                  return (
                                    <Tooltip title="Activate Course">
                                      <IconButton
                                        size="small"
                                        style={{ color: '#2e7d32' }}
                                        onClick={() => handleUpdateStatus(row._id, 'active')}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  );
                                } else if (status === 'active') {
                                  return (
                                    <Tooltip title="Close Course">
                                      <IconButton
                                        size="small"
                                        style={{ color: '#c62828' }}
                                        onClick={() => handleUpdateStatus(row._id, 'closed')}
                                      >
                                        <Stop fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  );
                                } else if (status === 'closed') {
                                  return (
                                    <Tooltip title="Course Closed">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled
                                          style={{ color: '#bdbdbd' }}
                                        >
                                          <Stop fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination Controls */}
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0rem' }}>
              <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '0rem 1rem' }}>
                <Typography style={{ color: maroon, fontSize: '0.85rem', fontWeight: 600 }}>
                  {data.total_count > 0 ? (page - 1) * offset + 1 : 0} -{' '}
                  {page * offset < data.total_count ? page * offset : data.total_count} of {data.total_count || 0}
                </Typography>
              </Box>
              <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => page > 1 && updatePage(page - 1)}
                  disabled={page === 1}
                >
                  <KeyboardArrowLeft style={{ color: page === 1 ? mediumGrey : headingBlue }} />
                </IconButton>

                <Button
                  variant="contained"
                  style={{
                    minWidth: '0rem',
                    width: '1.6rem',
                    height: '1.6rem',
                    borderRadius: '50%',
                    backgroundColor: headingBlue,
                    color: 'white',
                    padding: '0rem',
                    alignItems: 'center',
                    fontWeight: 600,
                    fontSize: '0.85rem'
                  }}
                >
                  {page}
                </Button>

                <IconButton
                  size="small"
                  onClick={() => page < data.total_pages && updatePage(page + 1)}
                  disabled={page === data.total_pages || !data.total_pages}
                >
                  <KeyboardArrowRight
                    style={{
                      color: page === data.total_pages || !data.total_pages ? mediumGrey : headingBlue
                    }}
                  />
                </IconButton>
              </Box>
              <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: '0rem 1rem' }}>
                <CustomDropDown
                  key="table_offset"
                  value={offset}
                  width="5rem"
                  setFunc={updateOffset}
                  list={tableOffsets}
                  regular={true}
                  extraParamvalue={true}
                />
              </Box>
            </Box>
          </TabPanel>

          {/* Tab 2: Results Dashboard */}
          <TabPanel value={tabValue} index={1}>


              {/* Metrics Top Tiles */}
              <Box sx={{ p: '0.4rem 1rem', position: 'relative' }}>
                {isTilesLoading && (
                  <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                    <CircularProgress size={24} style={{ color: headingBlue }} />
                  </Box>
                )}
                <Grid container spacing={1} sx={{ backgroundColor: '#f3f3f3', borderRadius: '0.5rem', mt: '0.3rem', p: '0rem 0.1rem 0.5rem 0.1rem' }}>
                  <Grid item xs={6} sm={3}>
                    <StatsBarCard
                      Icon={Group}
                      label="Total Attempts"
                      color={headingBlue}
                      data={{ count: tilesData.totalAttempts, percentage: 100 }}
                      selectedTile={null}
                      updateTile={() => {}}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatsBarCard
                      Icon={Group}
                      label="Inprogress attempts"
                      color="#ed6c02"
                      data={{
                        count: tilesData.inProgressAttempts,
                        percentage: tilesData.totalAttempts > 0 ? Math.round((tilesData.inProgressAttempts / tilesData.totalAttempts) * 100) : 0
                      }}
                      selectedTile={null}
                      updateTile={() => {}}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatsBarCard
                      Icon={Group}
                      label="Passed Attempts"
                      color="#2e7d32"
                      data={{
                        count: tilesData.passedAttempts,
                        percentage: tilesData.totalAttempts > 0 ? Math.round((tilesData.passedAttempts / tilesData.totalAttempts) * 100) : 0
                      }}
                      selectedTile={null}
                      updateTile={() => {}}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <StatsBarCard
                      Icon={Group}
                      label="Failed Attempts"
                      color="#d32f2f"
                      data={{
                        count: tilesData.failedAttempts,
                        percentage: tilesData.totalAttempts > 0 ? Math.round((tilesData.failedAttempts / tilesData.totalAttempts) * 100) : 0
                      }}
                      selectedTile={null}
                      updateTile={() => {}}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: '0rem 1rem' }}>
                <Paper style={paperStyle}>
                  {/* Header filter options */}
                  <Box style={headerBoxStyle}>
                    <Typography variant="h6" style={{ fontWeight: 600, color: headingBlue }}>
                      Quiz Attempt Results
                    </Typography>
                    <Box style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <CustomTextField
                        placeHolder="Enter Text to Filter"
                        value={resultsFilterBy}
                        setFunc={setResultsFilterBy}
                      />
                    </Box>
                  </Box>

                  {/* Results Table */}
                <Box style={{ display: 'flex', justifyContent: 'center', overflow: 'auto', position: 'relative' }}>
                  {isResultsLoading && (
                    <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                      <CircularProgress style={{ color: headingBlue }} />
                    </Box>
                  )}
                  <TableContainer
                    component={Paper}
                    sx={{
                      width: '100%',
                      height: '46vh',
                      border: '1px solid #D9D9D9',
                      marginTop: '0.2rem',
                      borderRadius: '6px',
                      '&::-webkit-scrollbar': { display: 'none' },
                      scrollbarWidth: 'none'
                    }}
                  >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell style={tableHeaderStyle}>User Name</TableCell>
                        <TableCell style={tableHeaderStyle}>User Contact</TableCell>
                        <TableCell style={tableHeaderStyle}>Chapter</TableCell>
                        <TableCell style={tableHeaderStyle}>District</TableCell>
                        <TableCell style={tableHeaderStyle}>WORKer Type</TableCell>
                        <TableCell style={tableHeaderStyle}>Attempt Date & Time</TableCell>
                        <TableCell style={tableHeaderStyle}>Status</TableCell>
                        <TableCell style={tableHeaderStyle}>Time Taken</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultsData?.data?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" style={{ padding: '2rem', color: '#888' }}>
                            No attempts found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        resultsData?.data?.map((row, idx) => (
                          <TableRow key={row._id || idx} hover>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem', color: headingBlue, fontWeight: 600 }}>
                              {row.userName || '-'}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                              {row.userContact || '-'}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                              {row.chapter || '-'}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                              {row.district || '-'}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                              {(() => {
                                const typeVal = row.WORKerType;
                                if (!typeVal || typeVal === 'N/A') return '-';
                                const match = WORKerTypesList.find(t => t.value === typeVal || t.value?.toLowerCase() === typeVal?.toLowerCase());
                                return match ? match.label : (typeVal.charAt(0).toUpperCase() + typeVal.slice(1));
                              })()}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.85rem' }}>
                              {row.attemptDateTime ? new Date(row.attemptDateTime).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                              {(() => {
                                const status = row.status;
                                let color = '#777';
                                if (status === 'Passed') color = '#2e7d32';
                                else if (status === 'Failed') color = '#c62828';
                                else if (status === 'InProgress') color = '#ed6c02';
                                return (
                                  <Box sx={{
                                    display: 'inline-block',
                                    border: `1px solid ${color}`,
                                    color: color,
                                    borderRadius: '4px',
                                    px: '0.4rem',
                                    py: '0.1rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    backgroundColor: `${color}0b`
                                  }}>
                                    {status}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="center" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                              {row.timeTaken || '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Results Table Pagination */}
              <Box style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0rem' }}>
                <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '0rem 1rem' }}>
                  <Typography style={{ color: maroon, fontSize: '0.85rem', fontWeight: 600 }}>
                    {resultsData.total_count > 0 ? (resultsPage - 1) * resultsOffset + 1 : 0} -{' '}
                    {resultsPage * resultsOffset < resultsData.total_count ? resultsPage * resultsOffset : resultsData.total_count} of {resultsData.total_count || 0}
                  </Typography>
                </Box>
                <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => resultsPage > 1 && updateResultsPage(resultsPage - 1)}
                    disabled={resultsPage === 1}
                  >
                    <KeyboardArrowLeft style={{ color: resultsPage === 1 ? mediumGrey : headingBlue }} />
                  </IconButton>

                  <Button
                    variant="contained"
                    style={{
                      minWidth: '0rem',
                      width: '1.6rem',
                      height: '1.6rem',
                      borderRadius: '50%',
                      backgroundColor: headingBlue,
                      color: 'white',
                      padding: '0rem',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }}
                  >
                    {resultsPage}
                  </Button>

                  <IconButton
                    size="small"
                    onClick={() => resultsPage < resultsData.total_pages && updateResultsPage(resultsPage + 1)}
                    disabled={resultsPage === resultsData.total_pages || !resultsData.total_pages}
                  >
                    <KeyboardArrowRight
                      style={{
                        color: resultsPage === resultsData.total_pages || !resultsData.total_pages ? mediumGrey : headingBlue
                      }}
                    />
                  </IconButton>
                </Box>
                <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: '0rem 1rem' }}>
                  <CustomDropDown
                    key="results_table_offset"
                    value={resultsOffset}
                    width="5rem"
                    setFunc={updateResultsOffset}
                    list={tableOffsets}
                    regular={true}
                    extraParamvalue={true}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        </TabPanel>
        </Box>

      {/* Course Quiz details modal */}
      <Dialog open={isDetailsOpen} onClose={() => setIsDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: headingBlue, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 1.25rem' }}>
          <Typography style={{ fontWeight: 600, fontSize: '1rem' }}>
            Course Details: {selectedCourse?.courseName} ({selectedCourse?.uniqueId})
          </Typography>
          <IconButton onClick={() => setIsDetailsOpen(false)} style={{ color: 'white', padding: 0 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '1.5rem' }}>
          <Grid container spacing={3}>
            {/* Left section: Videos List */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '0.5rem' }}>
                Course Videos ({selectedCourse?.videos?.length || 0})
              </Typography>
              <Paper variant="outlined" style={{ padding: '0.5rem', maxHeight: '35vh', overflowY: 'auto' }}>
                {selectedCourse?.videos?.map((vid, idx) => (
                  <Box
                    key={idx}
                    onClick={() => {
                      HttpService({
                        url: '/getTutorialVideoUrl',
                        method: 'GET',
                        params: { fileKey: vid.fileUrl },
                        headers: {
                          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                        }
                      }).then(res => {
                        if (res?.data?.data?.downloadUrl) {
                          setActiveVideoUrl(res.data.data.downloadUrl);
                        }
                      }).catch(err => {
                        console.error('Error fetching video URL:', err);
                        dispatch(showSnackBar({ message: 'Error loading video player.', severity: 'error' }));
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.4rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid #EEE',
                      backgroundColor: activeVideoUrl.includes(vid.fileUrl) ? '#F5F5F5' : 'transparent',
                      transition: 'background-color 0.2s',
                      borderRadius: '4px'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F5F5F5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <PlayCircleOutline style={{ color: headingBlue, marginRight: '0.5rem' }} />
                    <Typography style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {vid.title || `Video ${idx + 1}`}
                    </Typography>
                  </Box>
                ))}
              </Paper>

              {/* Inline video player */}
              {activeVideoUrl && (
                <Box style={{ marginTop: '1.25rem' }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '0.5rem' }}>
                    Video Player
                  </Typography>
                  <video
                    src={activeVideoUrl}
                    controls
                    controlsList="nodownload"
                    style={{ width: '100%', borderRadius: '4px', border: '1px solid #CCC', maxHeight: '200px', backgroundColor: 'black' }}
                  />
                </Box>
              )}
            </Grid>

            {/* Right section: Quiz / Assessment Configuration */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '0.5rem' }}>
                Course Metadata & Quiz
              </Typography>
              <Paper variant="outlined" style={{ padding: '1rem', minHeight: '35vh' }}>
                <Box style={{ marginBottom: '1rem' }}>
                  <Typography style={{ fontSize: '0.8rem', color: '#666' }}>Description</Typography>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                    {selectedCourse?.description || 'No description provided.'}
                  </Typography>
                </Box>
                <Box style={{ marginBottom: '1rem' }}>
                  <Typography style={{ fontSize: '0.8rem', color: '#666' }}>Is Quiz Enabled?</Typography>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: selectedCourse?.isQuiz ? headingBlue : '#888' }}>
                    {selectedCourse?.isQuiz ? 'Yes' : 'No'}
                  </Typography>
                </Box>

                {selectedCourse?.isQuiz && (
                  <>
                    <Box style={{ marginBottom: '1rem' }}>
                      <Typography style={{ fontSize: '0.8rem', color: '#666' }}>Attempts Allowed</Typography>
                      <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {selectedCourse?.attemptsAllowed}
                      </Typography>
                    </Box>
                    <Box style={{ marginBottom: '1rem' }}>
                      <Typography style={{ fontSize: '0.8rem', color: '#666' }}>Applicable For</Typography>
                      <Typography style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                        {getApplicableForLabel(selectedCourse)}
                      </Typography>
                    </Box>
                    <Box style={{ marginTop: '0.5rem' }}>
                      <AssessmentConfiguration
                        readOnly={true}
                        hideActions={true}
                        initialValues={{
                          isAssessmentRequired: true,
                          easyQty: selectedCourse?.assessmentConfig?.easyQuestions?.count || 0,
                          easyCredit: selectedCourse?.assessmentConfig?.easyQuestions?.credit || 0,
                          mediumQty: selectedCourse?.assessmentConfig?.mediumQuestions?.count || 0,
                          mediumCredit: selectedCourse?.assessmentConfig?.mediumQuestions?.credit || 0,
                          hardQty: selectedCourse?.assessmentConfig?.hardQuestions?.count || 0,
                          hardCredit: selectedCourse?.assessmentConfig?.hardQuestions?.credit || 0,
                          qualPercent: selectedCourse?.assessmentConfig?.qualificationPercentage || 0,
                          duration: selectedCourse?.assessmentConfig?.time || 30
                        }}
                      />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: '1rem 1.5rem' }}>
          <Button style={{ ...buttonStyle, backgroundColor: red }} onClick={() => setIsDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Course modal */}
      <AddCourseDialog
        dialogStatus={isAddOpen}
        updateDialogStatus={(status, refresh) => {
          setIsAddOpen(status);
          if (refresh) setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Course Questions modal */}
      <CourseQuestionsDialog
        dialogStatus={isQuestionsOpen}
        updateFunc={setIsQuestionsOpen}
        courseId={selectedCourse?._id}
        courseName={selectedCourse?.courseName}
      />
    </>
  );
};

export default Courses;
